import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useChangePasswordMutation } from '../services/authApi';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Flex } from '../components/shared/Flex';
import media from '../style/media';

import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard } from '../style/layout';

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  width: 100%;
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  padding-left: 0.25rem;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const FormInput = styled.input`
  padding: 0.875rem 3rem 0.875rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  width: 100%;
  color: #0f172a;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const ToggleIcon = styled.div`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: #3b82f6;
    background: #f1f5f9;
  }
`;

const Actions = styled.div`
  margin-top: 1rem;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.35);
    opacity: 0.95;
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SuccessAlert = styled.div`
  margin-bottom: 1.5rem;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #065f46;
  background: #ecfdf5;
  border: 1px solid #10b981;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ErrorAlert = styled.div`
  margin-bottom: 1.5rem;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #991b1b;
  background: #fef2f2;
  border: 1px solid #ef4444;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AccessDeniedMessage = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: rgba(254, 242, 242, 0.8);
  border: 1px solid rgba(254, 202, 202, 0.5);
  border-radius: 24px;
  color: #991b1b;

  h3 {
    color: #991b1b;
    margin-top: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  p {
    margin: 0.75rem 0;
    font-size: 1rem;
    opacity: 0.9;
  }
`;

const ChangePassword: React.FC = () => {
  const nav = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const cameras = useCameras();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [formError, setFormError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    const referrer = document.referrer;
    const isFromSettings = referrer.includes('/settings') ||
      referrer === '' ||
      referrer.includes(window.location.origin);

    if (!isFromSettings) {
      setShowAccessDenied(true);
      const timer = setTimeout(() => {
        nav('/settings');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [nav]);

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const updateLicenseStatus = useCallback((valid: boolean) => {
    setIsLicenseValid(valid);
  }, []);

  const [changePassword, { isLoading, error }] = useChangePasswordMutation();

  const validate = () => {
    if (!current || !next || !confirm) return 'All fields are required';
    if (next.length < 8) return 'New password must be at least 8 characters';
    if (next === current) return 'New password must be different from current password';
    if (next !== confirm) return 'New password and confirm password do not match';
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSuccessMsg(null);

    const v = validate();
    if (v) { setFormError(v); return; }

    try {
      const res = await changePassword({
        current_password: current,
        new_password: next
      }).unwrap();

      setSuccessMsg(res?.message || 'Password changed successfully');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (_) { }
  };

  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />
      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />
      <ContentWrapper>
        {showAccessDenied ? (
          <GlassCard>
            <AccessDeniedMessage>
              <h3>Access Denied</h3>
              <p>Please access Change Password through the Settings page.</p>
              <p>Redirecting you back to Settings...</p>
            </AccessDeniedMessage>
          </GlassCard>
        ) : (
          <GlassCard>
            <Header>
              <button 
                onClick={() => nav('/settings')} 
                style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: '#64748b', fontSize: '0.9rem', fontWeight: 600, padding: '0', marginBottom: '1.5rem', transition: 'color 0.2s', fontFamily: 'inherit' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#0f172a'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
              >
                <ArrowLeft size={18} /> Back to Settings
              </button>
              <Title>Change Password</Title>
              <Subtitle>Ensure your account stays secure by using a strong unique password.</Subtitle>
            </Header>

            <form onSubmit={onSubmit}>
              {(formError || error) && (
                <ErrorAlert>
                  <span>{formError || (error as any)?.data?.message || 'Failed to change password'}</span>
                </ErrorAlert>
              )}
              {successMsg && (
                <SuccessAlert>
                  <span>{successMsg}</span>
                </SuccessAlert>
              )}

              <FormField>
                <FormLabel htmlFor="current">Current Password</FormLabel>
                <InputWrapper>
                  <FormInput
                    id="current"
                    type={showCurrent ? 'text' : 'password'}
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    placeholder="Enter current password"
                  />
                  <ToggleIcon onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
                  </ToggleIcon>
                </InputWrapper>
              </FormField>

              <FormField>
                <FormLabel htmlFor="new">New Password</FormLabel>
                <InputWrapper>
                  <FormInput
                    id="new"
                    type={showNext ? 'text' : 'password'}
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    placeholder="Minimum 8 characters"
                  />
                  <ToggleIcon onClick={() => setShowNext(!showNext)}>
                    {showNext ? <EyeOff size={20} /> : <Eye size={20} />}
                  </ToggleIcon>
                </InputWrapper>
              </FormField>

              <FormField>
                <FormLabel htmlFor="confirm">Confirm New Password</FormLabel>
                <InputWrapper>
                  <FormInput
                    id="confirm"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat new password"
                  />
                  <ToggleIcon onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                  </ToggleIcon>
                </InputWrapper>
              </FormField>

              <Actions>
                <PrimaryButton type="submit" disabled={isLoading}>
                  {isLoading ? 'Updating...' : 'Update Password'}
                </PrimaryButton>
              </Actions>
            </form>
          </GlassCard>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default ChangePassword;
